<template>
  <div
    class="rc-input-container"
    v-click-outside="hide"
    :class="{ 'disabled-rc-input': disabled }"
  >
    <template v-if="floatLabel">
      <div
        class="rc-floating-label"
        :class="{
          'rc-label-active': isFocused || localValue,
          'rc-label-centered': !isFocused && !localValue,
        }"
        ref="floatingLabel"
      >
        <label for="" class="rc-floating-label__text">
          {{ label }}
        </label>
        <span
          v-show="requiredField"
          class="rc-required-field"
          ref="requiredField"
          >*</span
        >
      </div>
    </template>
    <div class="rc-form-group">
      <input
        v-model="localValue"
        @blur="_onBlur($event)"
        @focus="_onFocus($event)"
        @input="_onInput($event)"
        @change="_onChange($event)"
        @keydown="hoverSuggestion($event.key.toLowerCase())"
        :type="type"
        :min="min"
        :max="max"
        :disabled="disabled"
        :pattern="pattern"
        class="rc-input-field rc-default-input-styles"
        autocomplete="one-time-code"
      />
      <label
        v-if="!floatLabel && !localValue"
        class="rc-form-group__label"
        :class="{ 'rc-form-group__label--required': requiredField }"
        >{{ label }}</label
      >
    </div>
    <div class="rc-suggestions" v-if="suggestions.length && showSuggestions">
      <ul class="rc-suggestions-list" ref="suggestionsList">
        <li
          class="rc-suggestions-list__item"
          :class="
            index === hoveredSuggestionsIndex ? 'hover-item-background' : ''
          "
          @mousedown="selectSuggestion(suggestion)"
          v-for="(suggestion, index) in filteredSuggestions"
          :key="index"
        >
          <slot name="suggestion-item" :suggestion="suggestion">
            {{ suggestion }}
          </slot>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: "RCInput",
  directives: {
    "click-outside": {
      bind: (el, binding, vnode) => {
        el.clickOutsideEvent = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            vnode.context[binding.expression](event);
          }
        };
        document.body.addEventListener("click", el.clickOutsideEvent);
      },
      unbind: (el) => {
        document.body.removeEventListener("click", el.clickOutsideEvent);
      },
    },
  },
  props: {
    value: {
      required: false,
    },
    label: String,
    floatLabel: Boolean,
    type: {
      type: String,
      default: "text",
    },
    min: {
      type: Number,
      default: 0,
    },
    max: Number,
    disabled: {
      type: Boolean,
      default: false,
    },
    pattern: {
      type: String,
      required: false,
    },
    suggestions: {
      type: Array,
      required: false,
      default: () => [],
    },
        forceSelection: {
      type: Boolean,
      required: false,
      default: false,
    },
    positiveNumbers: {
      type: Boolean,
      required: false,
      default: false,
    },
    requiredField: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      localValue: this.value || "",
      isFocused: false,
      showSuggestions: false,
      filteredSuggestions: [...this.suggestions],
      hoveredSuggestionsIndex: -1,
      selectedSuggestion: null
    };
  },
  created() {
    this.filteredSuggestions = [...this.suggestions];
    if (this.forceSelection && this.suggestions.includes(this.value)) {
      this.selectedSuggestion = this.value        
    }
  },
  watch: {
    value(newValue) {
      this.localValue = newValue;
    },
  },
  methods: {
    getTextWidth(text, font) {
      const canvas =
        this.getTextWidth.canvas ||
        (this.getTextWidth.canvas = document.createElement("canvas"));
      const context = canvas.getContext("2d");
      context.font = font;
      const metrics = context.measureText(text);
      canvas.remove();
      return metrics.width;
    },
    getCssStyle(element, prop) {
      return window.getComputedStyle(element, null).getPropertyValue(prop);
    },
    getCanvasFont(el = document.body) {
      const fontWeight = this.getCssStyle(el, "font-weight") || "normal";
      const fontSize = this.getCssStyle(el, "font-size") || "16px";
      const fontFamily =
        this.getCssStyle(el, "font-family") || "Times New Roman";
      return `${fontWeight} ${fontSize} ${fontFamily}`;
    },
    _onInput(event) {
      this.selectedSuggestion = null;
      this.hoveredSuggestionsIndex = -1;
      if (this.positiveNumbers) {
        this.localValue = Math.abs(event.target.value);
      }
      this.filterSuggestions(event.target.value);
      this.$emit("input", event.target.value);
    },
    _onChange(event) {
      this.$emit("change", event.target.value);
    },
    _onBlur(event) {
      this.forceSelectionHandler();
      this.$emit("blur", event.target.value);
      this.isFocused = false;
      this.hoveredSuggestionsIndex = -1;
      this.showSuggestions = false;
    },
    _onFocus(event) {
      if (!this.localValue.length) {
        this.showSuggestions = true;
      }
      this.$emit("focus", event.target.value);
      this.isFocused = true;
    },
    selectSuggestion(suggestion) {
      this.selectedSuggestion = suggestion;
      this.$emit("input", suggestion);
      this.localValue = suggestion;
      this.showSuggestions = false;
      this.hoveredSuggestionsIndex = -1;
    },
    hoverSuggestion(event) {
      const arrowsKeys = ["arrowdown", "arrowup", "enter"];
      if (this.filteredSuggestions.length === 0 || !arrowsKeys.includes(event)) {
        return;
      }
      if (event === "enter" && this.hoveredSuggestionsIndex > -1) {
        this.selectSuggestion(this.filteredSuggestions[this.hoveredSuggestionsIndex]);
        return;
      }
      if (
        event === "arrowdown" &&
        this.hoveredSuggestionsIndex < this.filteredSuggestions.length - 1
      ) {
        this.hoveredSuggestionsIndex += 1;
      }
      if (event === "arrowup" && this.hoveredSuggestionsIndex > -1) {
        this.hoveredSuggestionsIndex -= 1;
      }

      this.$nextTick(() => {
        const selectedElement =
          this.$refs.suggestionsList.children[this.hoveredSuggestionsIndex];
        if (selectedElement) {
          selectedElement.scrollIntoView({
            block: "nearest",
            inline: "start",
          });
        }
      });
    },
    filterSuggestions(value) {
      this.filteredSuggestions = this.suggestions.filter((suggestion) =>
        suggestion.toLowerCase().includes(value.toLowerCase())
      );
      this.showSuggestions = this.filteredSuggestions.length > 0;
    },
    forceSelectionHandler() {
      if (this.forceSelection && !this.selectedSuggestion) {
        this.localValue = "";
        this.$emit("input", "");
      }
    },
    hide() {
      this.showSuggestions = false;
    },
  },
};
</script>

<style scoped>
@import "../styles/input.css";

.rc-input-container {
  position: relative;
  display: flex;
  align-items: center;
  width: 200px;
  height: 40px;
  transition: all 0.3s;
  font-size: 16px;
  color: #888;
  border: 1px solid #939393;
  border-radius: 10px;
  background-color: white;
}

.disabled-rc-input {
  background-color: #efefef;
}

.rc-input-field {
  font-size: 16px;
  height: 100%;
  background: none;
}

.rc-label-active {
  font-size: 12px;
  transform: translateY(-18px);
}

.rc-label-centered {
  width: 90%;
  top: 52%;
  left: 5px;
  transform: translateY(-50%);
  text-overflow: ellipsis;
  overflow: hidden;
}

.hover-item-background {
  background-color: #e9ecef;
}

.rc-suggestions {
  position: absolute;
  top: 40px;
  left: 0px;
  width: 100%;
  height: auto;
  max-height: 150px;
  overflow-y: scroll;
  border: 1px solid rgba(196, 196, 196, 0.767);
  background-color: white;
  z-index: 10;
}

.rc-suggestions-list {
  padding: 0;
  margin: 0;
  list-style-type: none;
  text-align: left;
}

.rc-suggestions-list__item {
  padding: 5px 10px;
  cursor: pointer;
}

.rc-suggestions-list__item:hover {
  background-color: #ededed;
}

.rc-suggestions::-webkit-scrollbar {
  width: 5px;
}
.rc-suggestions::-webkit-scrollbar-track {
  box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  border-radius: 10px;
}
.rc-suggestions::-webkit-scrollbar-thumb {
  border-radius: 10px;
  box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.5);
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
}
</style>
